import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface FallbackProps {
  error: Error;
  componentStack: string;
  resetError: () => void;
}

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetError }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark px-4">
      <div className="card max-w-lg w-full p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
            <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
          </div>
        </div>
        
        <h1 className="text-2xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
          Something went wrong
        </h1>
        
        <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
          We&apos;re sorry, but something unexpected happened. Our team has been notified and we&apos;re working on a fix.
        </p>

        {import.meta.env.MODE === 'development' && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg text-left">
            <p className="font-mono text-sm text-red-800 dark:text-red-300 break-all">
              {error.message}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={resetError}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
          
          <button
            onClick={() => window.location.href = '/'}
            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark text-text-primary dark:text-text-dark-primary rounded-lg font-medium hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
          >
            <Home className="w-4 h-4" />
            Go Home
          </button>
        </div>

        <p className="mt-6 text-sm text-text-tertiary dark:text-text-dark-tertiary">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
};

// Create the ErrorBoundary with Sentry integration
const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => <ErrorFallback {...errorData} />}
      showDialog={false}
      beforeCapture={(scope) => {
        // Add additional context before capturing
        scope.setTag('error_boundary', 'app_root');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;
